import { useState, useRef, useCallback } from 'react';

// Constants for rate limiting
const RATE_LIMIT_WINDOW_MS = 60 * 1000; // 1 minute
const MAX_SUBMISSIONS_PER_WINDOW = 3;

interface FormProtectionState {
  honeypotValue: string;
  submissionCount: number;
  windowStart: number;
}

interface UseFormProtectionReturn {
  // Honeypot field props - invisible to humans, filled by bots
  honeypotProps: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    tabIndex: number;
    autoComplete: string;
    'aria-hidden': boolean;
    style: React.CSSProperties;
    name: string;
  };
  // Validation check before submission
  validateSubmission: () => { valid: boolean; error?: string };
  // Track successful submission for rate limiting
  recordSubmission: () => void;
  // Check if rate limited
  isRateLimited: boolean;
  // Remaining submissions in window
  remainingSubmissions: number;
}

const STORAGE_KEY = 'form_protection_state';

function getStoredState(): FormProtectionState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored) as FormProtectionState;
      // Reset if window expired
      if (Date.now() - state.windowStart > RATE_LIMIT_WINDOW_MS) {
        return { honeypotValue: '', submissionCount: 0, windowStart: Date.now() };
      }
      return state;
    }
  } catch {
    // Ignore storage errors
  }
  return { honeypotValue: '', submissionCount: 0, windowStart: Date.now() };
}

function storeState(state: FormProtectionState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

export function useFormProtection(formId: string = 'default'): UseFormProtectionReturn {
  const [honeypotValue, setHoneypotValue] = useState('');
  const stateRef = useRef<FormProtectionState>(getStoredState());
  const [, forceUpdate] = useState({});

  // Check if current window has expired and reset if needed
  const getCurrentState = useCallback((): FormProtectionState => {
    const state = stateRef.current;
    if (Date.now() - state.windowStart > RATE_LIMIT_WINDOW_MS) {
      stateRef.current = { honeypotValue: '', submissionCount: 0, windowStart: Date.now() };
      storeState(stateRef.current);
    }
    return stateRef.current;
  }, []);

  const validateSubmission = useCallback((): { valid: boolean; error?: string } => {
    // Check honeypot - if filled, it's a bot
    if (honeypotValue.trim() !== '') {
      // Silently fail - don't let bots know they were caught
      console.warn(`[FormProtection:${formId}] Honeypot triggered`);
      return { valid: false, error: 'Submission failed. Please try again.' };
    }

    // Check rate limit
    const state = getCurrentState();
    if (state.submissionCount >= MAX_SUBMISSIONS_PER_WINDOW) {
      const remainingMs = RATE_LIMIT_WINDOW_MS - (Date.now() - state.windowStart);
      const remainingSecs = Math.ceil(remainingMs / 1000);
      return { 
        valid: false, 
        error: `Too many submissions. Please wait ${remainingSecs} seconds before trying again.` 
      };
    }

    return { valid: true };
  }, [honeypotValue, getCurrentState, formId]);

  const recordSubmission = useCallback(() => {
    const state = getCurrentState();
    stateRef.current = {
      ...state,
      submissionCount: state.submissionCount + 1,
    };
    storeState(stateRef.current);
    forceUpdate({});
  }, [getCurrentState]);

  const currentState = getCurrentState();
  const isRateLimited = currentState.submissionCount >= MAX_SUBMISSIONS_PER_WINDOW;
  const remainingSubmissions = Math.max(0, MAX_SUBMISSIONS_PER_WINDOW - currentState.submissionCount);

  const honeypotProps = {
    value: honeypotValue,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setHoneypotValue(e.target.value),
    tabIndex: -1,
    autoComplete: 'off',
    'aria-hidden': true as const,
    name: `${formId}_website`, // Common bot-attracting field name
    style: {
      position: 'absolute' as const,
      left: '-9999px',
      top: '-9999px',
      opacity: 0,
      height: 0,
      width: 0,
      overflow: 'hidden' as const,
      pointerEvents: 'none' as const,
    },
  };

  return {
    honeypotProps,
    validateSubmission,
    recordSubmission,
    isRateLimited,
    remainingSubmissions,
  };
}
